/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ApolloCache } from "@apollo/client";
import {
  OrgUnitBudgetDocument,
  OrgUnitBudgetQuery,
  OrgUnitBudgetQueryVariables,
  SetOrgUnitYearlyBudgetMutation,
  useSetOrgUnitYearlyBudgetMutation,
} from "~/generated/graphql";
import { newMonthValues } from "~/components/Budgets";
import { showSuccess } from "~/services/toast";
import {
  generateActionSuccessText,
  handleError,
  Maybe,
  toInputError,
} from "~/tools";

export function useCreateBudget() {
  const [
    createOrgUnitBudgetMutation,
    {
      client: { cache },
      ...state
    },
  ] = useSetOrgUnitYearlyBudgetMutation();

  const create = async ({ nid, year, budgetValue, currency }: CreateProps) => {
    try {
      const { data } = await createOrgUnitBudgetMutation({
        variables: {
          nid,
          budget: {
            budgetByMonth: newMonthValues(budgetValue),
            year,
            currency,
          },
        },
      });

      handleCacheUpdate(data, cache, {
        nid,
        year,
        previousYear: (parseInt(year, 10) - 1).toString(),
      });

      showSuccess(generateActionSuccessText("Budget")()("created")());
    } catch (error) {
      handleError(toInputError(error));
    }
  };

  return [create, state] as const;
}

function handleCacheUpdate(
  data: Maybe<SetOrgUnitYearlyBudgetMutation>,
  cache: ApolloCache<unknown>,
  variables: OrgUnitBudgetQueryVariables
) {
  cache.updateQuery<OrgUnitBudgetQuery, OrgUnitBudgetQueryVariables>(
    { query: OrgUnitBudgetDocument, variables },
    (orgUnitData): OrgUnitBudgetQuery | null => {
      if (orgUnitData?.orgUnitV2 == null || data?.setOrgUnitBudget == null) {
        return null;
      }

      return {
        ...orgUnitData,
        orgUnitV2: {
          ...orgUnitData.orgUnitV2,
          budgetYearly: {
            ...data.setOrgUnitBudget,
          },
        },
      };
    }
  );
}

type CreateProps = {
  nid: string;
  year: string;
  budgetValue: number;
  currency: string;
};
